/**
 * AHA-47691. Mixpanel events for the FAB assistant (the docked /sandbox/ or
 * /master-agent/ panel). Tracking only: every helper swallows its own errors, so a
 * tracking failure can never change what the panel or a tool does.
 *
 * The click events stay where they are (`click_assistant_launcher_button` and friends,
 * sent by v-aha-emit-action). These are the STATE changes: one `assistant_panel_opened`
 * per closed -> open, one `assistant_panel_closed` per open -> closed, whoever caused it.
 */

import Config from '@/config'
import { ROUTE_NAMES } from '@/constant/routes'

// The sender is handed in, not imported: mixpanel.utils imports the store, and this file
// is imported BY the store (ai-chatbot.module), so importing it here would be an import
// cycle. mixpanel.utils registers its `track` when it loads (the store's own mixpanel
// plugin loads it at start-up); until then, and in tests that do not set one, nothing
// is sent.
let tracker = null

/** @param {Function|null} track  (eventName, props) => void, i.e. mixpanel.utils.track */
export const setAssistantTracker = (track) => {
  tracker = typeof track === 'function' ? track : null
}

export const ASSISTANT_PANEL_OPENED = 'assistant_panel_opened'
export const ASSISTANT_PANEL_CLOSED = 'assistant_panel_closed'
export const ASSISTANT_OPEN_ITEM = 'assistant_open_item'
// The name v-aha-emit-action.view would give the launcher (`name="assistant-launcher-button"`).
export const ASSISTANT_LAUNCHER_VIEW = 'view_assistant_launcher_button'

/** What opened or closed the panel. `unknown` = a caller that does not say. */
export const ASSISTANT_PANEL_SOURCES = Object.freeze({
  LAUNCHER_BUTTON: 'launcher_button',
  HELP_CENTER: 'help_center',
  OPEN_ASSISTANT_QUERY: 'open_assistant_query',
  EMBED_AI_BUTTON: 'embed_ai_button',
  HOST_REQUEST: 'host_request',
  HOST_REQUEST_DROPPED: 'host_request_dropped',
  IMPORT: 'import',
  // The panel was open when the page loaded (state rehydrated from sessionStorage).
  RESTORED: 'restored',
  CLOSE_BUTTON: 'close_button',
  PANEL_REQUEST: 'panel_request',
  UNKNOWN: 'unknown',
})

export const ASSISTANT_ENGINES = Object.freeze({
  MASTER_AGENT: 'master-agent',
  SANDBOX: 'sandbox',
})

/** The engine behind the panel: the same flag that makes buildAssistantPanelUrl pick
 *  /master-agent/ over /sandbox/. */
export const assistantEngine = (rootGetters) => (
  rootGetters && rootGetters['featureToggles/canUseMasterAgentEditor'] === true
    ? ASSISTANT_ENGINES.MASTER_AGENT
    : ASSISTANT_ENGINES.SANDBOX
)

// Routes whose `:id` IS the presentation. There the id is known from the route at once;
// the store's presentation can still be the previous deck while the next one loads.
// Read at call time, not at load: this file is loaded early through mixpanel.utils.
const DECK_ROUTE_KEYS = [
  'PRESENTATION_DETAIL',
  'PRESENTATION_REPORT',
  'PRESENTATION_DETAIL_GOOGLE_SLIDE',
  'PRESENTATION_AUDIENCE_TEST',
  'PRESENTATION_BACKSTAGE',
  'PPT_ADDIN_EDIT_SLIDES',
]
const isDeckRoute = (name) => Boolean(name)
  && DECK_ROUTE_KEYS.some((key) => ROUTE_NAMES && ROUTE_NAMES[key] === name)

/**
 * page_name and, only when it is certain, presentation_id. An event-level
 * presentation_id overrides the super property, so a stale one is worse than none:
 * on a deck route it is the route's id; elsewhere the store's deck only when the route
 * names that same deck; otherwise it is left out.
 */
const pageProps = (rootState) => {
  const route = (rootState && rootState.route) || null
  const routeId = route && route.params && route.params.id
  const storeId = rootState && rootState.presentation && rootState.presentation.id
  let presentationId = null
  if (route && isDeckRoute(route.name)) {
    presentationId = routeId || null
  } else if (storeId != null && routeId != null && String(storeId) === String(routeId)) {
    presentationId = storeId
  }
  const pageName = (route && route.name) || null
  return {
    ...(presentationId ? { presentation_id: presentationId } : {}),
    ...(pageName ? { page_name: pageName } : {}),
  }
}

const safeTrack = (eventName, props) => {
  if (!tracker) return
  try {
    tracker(eventName, props)
  } catch (e) {
    // tracking must never break the assistant
  }
}

// Panel events that waited for the user's Unleash pass: { eventName, props } without
// `engine`, which is read when they go out. Capped so a page whose flags never settle
// cannot grow it without bound.
const MAX_DEFERRED = 20
let deferredPanelEvents = []

const flagsSettled = (rootGetters) => Boolean(rootGetters && rootGetters['featureToggles/hasSettledAuthFlags'])

/**
 * Send the panel events that waited for the flags, in order, with the engine of the
 * settled flags. No-op until `featureToggles/hasSettledAuthFlags`.
 */
export const flushDeferredAssistantEvents = (rootGetters) => {
  if (!flagsSettled(rootGetters) || !deferredPanelEvents.length) return
  const queued = deferredPanelEvents
  deferredPanelEvents = []
  const engine = assistantEngine(rootGetters)
  queued.forEach(({ eventName, props }) => safeTrack(eventName, { ...props, engine }))
}

const sourceOf = (payload) => (payload && typeof payload.source === 'string' && payload.source)
  || ASSISTANT_PANEL_SOURCES.UNKNOWN

/**
 * @param {boolean} opened   true = the panel just opened, false = it just closed
 * @param {Object}  context  the Vuex action context (state / rootState / rootGetters)
 * @param {Object}  payload  the action payload, `{ source }`
 *
 * Not in the PowerPoint add-in (its triggers open a browser window, not the panel).
 * Until the user's Unleash pass has settled the event waits, so `engine` is not read
 * from the anonymous / cached flags; the props are taken now.
 */
export const trackAssistantPanelChange = (opened, context, payload) => {
  try {
    if (Config.isEnableAddinPowerpoint) return
    const { state, rootState, rootGetters } = context || {}
    const lastPresentationId = state && state.lastKnownPresentationId
    const props = {
      source: sourceOf(payload),
      ...pageProps(rootState),
      ...(lastPresentationId ? { last_presentation_id: lastPresentationId } : {}),
    }
    const eventName = opened ? ASSISTANT_PANEL_OPENED : ASSISTANT_PANEL_CLOSED
    if (flagsSettled(rootGetters)) {
      flushDeferredAssistantEvents(rootGetters)
      safeTrack(eventName, { ...props, engine: assistantEngine(rootGetters) })
    } else if (deferredPanelEvents.length < MAX_DEFERRED) {
      deferredPanelEvents.push({ eventName, props })
    }
  } catch (e) {
    // see safeTrack
  }
}

/** The status a host open tool answered with, or `unknown`. */
const outcomeOf = (result) => (result && result.output && result.output.status) || 'unknown'

/**
 * `assistant_open_item`: a host open tool (open_presentation / open_survey /
 * open_course) answered. Wraps the tool body: `run()` is called once, as before, and
 * its result (or the promise of one — pushAndClear returns a promise when the editor
 * had to save first) is returned untouched. presentation_id / page_name are read
 * BEFORE run(), so they describe the page the user opened the item from.
 * The chat app sends a link click and a model tool call as the same tool call, so the
 * two cannot be told apart here and no `triggered_by` is sent.
 *
 * @param {Object}   store     the Vuex store
 * @param {string}   itemType  presentation | survey | course | lesson
 * @param {Function} run       the tool body: () => result | Promise<result>
 */
export const withOpenItemTracking = (store, itemType, run) => {
  let base = null
  try {
    base = {
      item_type: itemType,
      engine: assistantEngine(store && store.getters),
      ...pageProps(store && store.state),
    }
  } catch (e) {
    base = null
  }
  const send = (settled) => {
    if (!base) return
    try {
      safeTrack(ASSISTANT_OPEN_ITEM, { ...base, outcome: outcomeOf(settled) })
    } catch (e) {
      // see safeTrack
    }
  }
  const failed = { output: { status: 'tool_failed' } }
  let result
  try {
    result = run()
  } catch (error) {
    // Reported, then rethrown untouched: the caller sees exactly what it saw before.
    send(failed)
    throw error
  }
  if (result && typeof result.then === 'function') {
    // A side branch: the caller still gets `result` itself, rejection included.
    result.then(send, () => send(failed)).catch(() => {})
  } else {
    send(result)
  }
  return result
}

/** Host open tool -> (input) => the item_type it opens. */
const OPEN_ITEM_TOOLS = Object.freeze({
  open_presentation: () => 'presentation',
  open_survey: () => 'survey',
  open_course: (input) => {
    const lessonId = input && input.lessonId
    return lessonId !== undefined && lessonId !== null && lessonId !== '' ? 'lesson' : 'course'
  },
})

/**
 * Wrap the host open tools of a tool registry (in place) with withOpenItemTracking.
 * Each wrapper calls the original handler with the same input, once, and returns
 * what it returned; a tool the registry does not have is left alone.
 *
 * @param {Object} registry  tool name -> handler
 * @param {Object} store     the Vuex store
 * @returns {Object} the same registry
 */
export const trackOpenItemTools = (registry, store) => {
  if (!registry) return registry
  Object.keys(OPEN_ITEM_TOOLS).forEach((tool) => {
    const handler = registry[tool]
    if (typeof handler !== 'function') return
    // eslint-disable-next-line no-param-reassign
    registry[tool] = function trackedOpenItemTool(input, ...rest) {
      let itemType = 'unknown'
      try {
        itemType = OPEN_ITEM_TOOLS[tool](input)
      } catch (e) {
        itemType = 'unknown'
      }
      return withOpenItemTracking(store, itemType, () => handler.call(this, input, ...rest))
    }
  })
  return registry
}

// The page the launcher impression was last sent for. Module scope, so a remount of the
// launcher on the same page (after the panel closes) does not count a second view.
let lastLauncherImpressionKey = null

/**
 * `view_assistant_launcher_button`, once per page (`pageKey`: the route path) while
 * the bubble is shown.
 *
 * @param {Object} store    the Vuex store
 * @param {string} pageKey  identifies the page view
 */
export const trackAssistantLauncherImpression = (store, pageKey) => {
  if (!pageKey || pageKey === lastLauncherImpressionKey) return
  lastLauncherImpressionKey = pageKey
  try {
    safeTrack(ASSISTANT_LAUNCHER_VIEW, {
      engine: assistantEngine(store && store.getters),
      ...pageProps(store && store.state),
    })
  } catch (e) {
    // see safeTrack
  }
}

/** Test seam. */
export function resetAssistantTrackingForTests() {
  lastLauncherImpressionKey = null
  deferredPanelEvents = []
}
